import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from "react";
import { trackKlaviyo, api } from "@/lib/api";
import { trackAddToCart } from "@/lib/analytics";

const CartContext = createContext(null);
const STORAGE_KEY = "noir_cart_v2";
const EMAIL_KEY = "doria_customer_email";

function getKnownEmail() {
  try {
    // Check direct email storage
    const direct = localStorage.getItem(EMAIL_KEY);
    if (direct) return direct;
    // Check quiz answers
    const quiz = localStorage.getItem("doria_quiz_answers_v1");
    if (quiz) {
      const parsed = JSON.parse(quiz);
      if (parsed?.email) return parsed.email;
    }
    // Check newsletter
    const nl = localStorage.getItem("doria_newsletter_v1");
    if (nl) {
      const parsed = JSON.parse(nl);
      if (parsed?.email) return parsed.email;
    }
  } catch {}
  return null;
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [isOpen, setIsOpen] = useState(false);

  // Clear cart if returning from a successful Shopify checkout
  useEffect(() => {
    try {
      const pending = localStorage.getItem("doria_checkout_pending");
      if (pending) {
        localStorage.removeItem("doria_checkout_pending");
        setItems([]);
      }
    } catch {}
  }, []);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); }
    catch (err) { console.warn("cart write failed", err); }
  }, [items]);

  // Save cart to backend for abandoned cart recovery (debounced)
  useEffect(() => {
    if (items.length === 0) return;
    const email = getKnownEmail();
    if (!email) return;
    const timer = setTimeout(() => {
      const cartItems = items.map((i) => ({
        name: i.product.name,
        brand: i.product.brand || "",
        image: i.product.image || "",
        price: i.variant.price,
        variant_id: i.variant.id,
        handle: i.product.id?.replace?.(/^gid:\/\/shopify\/Product\//, '') || i.product.id,
        quantity: i.quantity,
      }));
      api.post("/cart/save", { email, items: cartItems }).catch(() => {});
    }, 2000);
    return () => clearTimeout(timer);
  }, [items]);

  const addItem = (product, variant, quantity = 1) => {
    setItems((prev) => {
      const idx = prev.findIndex((i) => i.variant.id === variant.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + quantity };
        return next;
      }
      return [...prev, { product, variant, quantity }];
    });
    setIsOpen(true);
    trackAddToCart(product, variant, quantity);
    trackKlaviyo("Added to Cart", {
      product_id: product.id,
      product_name: product.name,
      brand: product.brand,
      price: variant.price,
      quantity,
      variant_id: variant.id,
    });
  };

  const removeItem = (variantId) => {
    setItems((prev) => prev.filter((i) => i.variant.id !== variantId));
  };

  const updateQty = (variantId, quantity) => {
    setItems((prev) =>
      prev.map((i) => (i.variant.id === variantId ? { ...i, quantity } : i))
        .filter((i) => i.quantity > 0)
    );
  };

  const clear = () => setItems([]);

  // Mark cart as purchased when checkout completes
  const markPurchased = useCallback(() => {
    const email = getKnownEmail();
    if (email) {
      api.post("/cart/purchased", { email }).catch(() => {});
    }
  }, []);

  const subtotal = useMemo(
    () => items.reduce((s, i) => s + i.variant.price * i.quantity, 0),
    [items]
  );
  const count = useMemo(() => items.reduce((s, i) => s + i.quantity, 0), [items]);

  const value = { items, isOpen, setIsOpen, addItem, removeItem, updateQty, clear, subtotal, count, markPurchased };
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
